import React, { useMemo, useEffect } from 'react';
import { __t } from '../../../../../base/utils/t';
import { useAppDispatch, useAppSelector } from '../../../../../base/hooks';
import { quickApplyActions } from '../../redux/slices/quickApplySlice';
import { Select, SelectOption } from '../../../../../base/components/Forms/Select';
import { Input } from '../../../../../base/components/Forms/Input';
import { CityAutocomplete } from '../../../../../base/components/CityAutocomplete';
import { workerAppActions } from '../../../../redux/slices/workerAppSlice';
import { City } from '../../../../../base/interfaces/config';

import './styles.scss';

export const PersonalInfo = () => {
    const dispatch = useAppDispatch();
    const {
        fields: { name, age, citizenshipCountryId, city },
        errors,
    } = useAppSelector((state) => state.workerAppQuickApply);
    const {
        citySearchOptions,
        config: { citizenshipCountries },
    } = useAppSelector((state) => state.workerApp);

    useEffect(() => {
        const defaultCitizenshipCountry = citizenshipCountries.find((i) => i.code === 'ua');
        if (defaultCitizenshipCountry) {
            dispatch(
                quickApplyActions.setField({
                    id: 'citizenshipCountryId',
                    value: defaultCitizenshipCountry.id.toString(),
                }),
            );
        }
    }, [citizenshipCountries]);

    const nameChangeHandler = (value: string) => {
        dispatch(quickApplyActions.setField({ id: 'name', value }));
    };

    const ageChangeHandler = (value: string) => {
        if (Number.isNaN(Number(value))) {
            return;
        }
        if (value === '') {
            dispatch(quickApplyActions.setField({ id: 'age', value }));
            return;
        }

        const numberValue = Number(value);
        if (numberValue <= 0 || numberValue >= 100) {
            return;
        }
        dispatch(quickApplyActions.setField({ id: 'age', value: numberValue }));
    };

    const citizenshipChangeHandler = (id: string, value: unknown) => {
        dispatch(quickApplyActions.setField({ id, value: value as string }));
    };

    const selectOptions: SelectOption[] = useMemo(() => {
        return [...citizenshipCountries]
            .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
            .map((i) => ({ value: i.id.toString(), text: i.name }));
    }, [citizenshipCountries]);

    const popularOptions: SelectOption[] = useMemo(
        () => citizenshipCountries.filter((i) => i.isPopular).map((i) => ({ value: i.id.toString(), text: i.name })),
        [citizenshipCountries],
    );

    const citySearchHandler = (value: string) => dispatch(workerAppActions.citySearch(value));

    const citySetSearchOptionsHandler = (options: City[] | null) => dispatch(workerAppActions.setCitySearch(options));

    const citySelectHandler = (value: City | null) =>
        dispatch(quickApplyActions.setField({ id: 'city', value, errorGroupName: 'cityId' }));

    return (
        <div className='quick-apply-step-form'>
            <div className='quick-apply-step-form__item'>
                <p className='quick-apply-step-form__label'>{__t('Как Вас зовут?')}</p>
                <Input
                    value={name}
                    placeholder={__t('Введите имя')}
                    className='quick-apply-step-form__input'
                    onChange={nameChangeHandler}
                    errors={errors?.name}
                />
            </div>

            <div className='quick-apply-step-form__item'>
                <p className='quick-apply-step-form__label'>{__t('Сколько вам лет?')}</p>
                <Input
                    value={age as unknown as string}
                    placeholder={__t('Ваш возраст')}
                    className='quick-apply-step-form__input'
                    onChange={ageChangeHandler}
                    errors={errors?.age}
                    inputmode='numeric'
                />
            </div>

            <div className='quick-apply-step-form__item'>
                <p className='quick-apply-step-form__label'>{__t('В каком городе находитесь?')}</p>
                <CityAutocomplete
                    appType='worker'
                    options={citySearchOptions}
                    selectedOption={city}
                    errors={errors?.cityId}
                    onSearch={citySearchHandler}
                    setSearchOptions={citySetSearchOptionsHandler}
                    onSelect={citySelectHandler}
                />
            </div>

            <div className='quick-apply-step-form__item'>
                <p className='quick-apply-step-form__label'>{__t('Ваше гражданство?')}</p>
                <Select
                    id='citizenshipCountryId'
                    value={citizenshipCountryId}
                    options={selectOptions}
                    popularOptions={popularOptions}
                    onChange={citizenshipChangeHandler}
                    className='quick-apply-step-form__select'
                    dropdownClassname='quick-apply-step-form__dropdown'
                    popularOptionsTitle={__t('Популярное')}
                    allOptionsTitle={__t('Все страны')}
                />
            </div>
        </div>
    );
};
